import {
  useBulkDeleteFormSubmissions,
  useExportFormSubmissions,
} from "#hooks/adapters/useFormSubmissions";
import { useNavigate } from "#src/Routers/hooks";
import { linkToFormSubmissionDetail } from "#src/Routers/links";
import { ImportDataAction } from "#src/batteries-included-components";
import { BulkUpdateProgressBanner } from "#src/batteries-included-components/Banners/BulkUpdateProgressBanner/BulkUpdateProgressBanner";
import { BulkDeleteFormSubmissionDialog } from "#src/batteries-included-components/Dialogs/BulkDeleteFormSubmissionsDialog";
import {
  FormSubmissionsTableFilterArea,
  FormSubmissionsTableFilterAreaDrawerContent,
} from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import {
  getFilteredSubmissionIdsByAnswerSearch,
  getFormSubmissionQueryPayloadWithAnswerFilter,
  getFormattedFiltersBasedOnFormSchema,
  getHeadersFromSchema,
} from "#src/batteries-included-components/Panels/TablePanels/FormSubmissionsTablePanel.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { getStatusType } from "#src/components/Common/Table/rendererHelper";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import {
  useBulkExportFormSubmissionAsPDF,
  useExportFormSubmissionAsPDF,
} from "#src/components/Forms/useExportFormSubmissionAsPDF";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import {
  useHasPermission,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { linkToUpdateFormSubmission } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]/update-form-submission";
import { useDataTableUserInterfaceConfiguration } from "#src/utils/table";
import {
  UseQueryOptions,
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Button,
  ButtonWithPopover,
  DataTable,
  DataTablePanel,
  DropdownMenu,
  HeaderType,
  Icon,
  ItemAction,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  FormSchemaAdapter,
  FormSubmissionAdapter,
  FormSubmissionStatus,
  FormSubmissionType,
  ResourceDefinitions,
  Resources,
  SortDirection,
  UserReturnType,
  UsersAdapter,
  type FormSubmissionGetListRequestType,
} from "@validereinc/domain";
import { toFlattenedObject, toTitleCaseString } from "@validereinc/utilities";
import { isEmpty } from "es-toolkit/compat";
import React, { useEffect, useMemo, useState } from "react";
import { FormSubmissionsDownloadPDFsRoute } from "../../../routes/forms/categories/[categoryId]/templates/[formTemplateId]/download-form-submissions-pdfs/index";

export const FormSubmissionsTablePanel = ({
  isDraft = false,
  formSchemaId,
  formCategoryId,
  filterConfigStorageKey,
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: {
  isDraft?: boolean;
  formSchemaId: string;
  formCategoryId?: string;
} & Pick<
  StorageKeys,
  | "filterConfigStorageKey"
  | "viewConfigStorageKey"
  | "tableConfigStorageKey"
  | "tableStateStorageKey"
>) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const [canDeleteFormSubmissions] = useHasPermission(
    "form_submissions:delete"
  );
  const [canEditFormSubmissions] = useHasPermission("form_submissions:edit");
  const [selectionStorageKey] = useStorageKey(
    ["download-selection"],
    "form-submissions-table"
  );

  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);

  const [viewFilters] = useStickyState<
    FormSubmissionGetListRequestType["filters"]
  >({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState<
    FormSubmissionGetListRequestType["filters"]
  >({}, filterConfigStorageKey);
  const [, setDownloadSelection] = useStickyState<{
    form_submission_ids: string[];
  }>({ form_submission_ids: [] }, selectionStorageKey);
  const filters = useMemo(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const {
    created_at,
    status,
    created_by,
    dataset_submission_id,
    ...restFilters
  } = filters;
  const formSchemaIdFilter = formSchemaId ?? restFilters["form_schema.id"];
  const [{ sortBy, sortDirection, ...pagination }, setTableState] =
    useTableSortingAndPagination(
      {
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      filters,
      tableStateStorageKey
    );
  const [selectedFormSubmissions, setSelectedFormSubmissions] = useState<
    Record<string, FormSubmissionType>
  >({});

  const [isSelectionInverted, setIsSelectionInverted] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const schemaQuery = useQuery({
    queryKey: [Resources.FORM_SCHEMA, formSchemaId],
    queryFn: () => {
      if (!formSchemaId) return;

      return FormSchemaAdapter.getOne({
        id: formSchemaId,
      });
    },
    enabled: Boolean(formSchemaId),
    select: (resp) => resp?.data,
    staleTime: 2 * 60 * 1000,
  });

  const formattedAnswerFilters = useMemo(
    () =>
      schemaQuery.data
        ? getFormattedFiltersBasedOnFormSchema(restFilters, schemaQuery.data)
        : null,
    [restFilters, schemaQuery]
  );

  const hasFormattedAnswerFilters = !isEmpty(formattedAnswerFilters);
  const answersQueryFilter = {
    created_at,
    created_by,
    dataset_submission_id,
    ...toFlattenedObject({
      status: isDraft
        ? FormSubmissionStatus.DRAFT
        : status
          ? status
          : Object.values(FormSubmissionStatus).filter(
              (s) => s !== FormSubmissionStatus.DRAFT
            ),
    }),
    ...formattedAnswerFilters,
  };

  const {
    data: answersQuery,
    hasNextPage: answersQueryHasNextPage,
    isFetching: answersQueryIsFetching,
    isLoading: answersQueryIsLoading,
    fetchNextPage: answersQueryFetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "forms",
      "submissions",
      "answers",
      formSchemaId,
      answersQueryFilter,
    ],
    queryFn: ({ pageParam }) => {
      if (!formSchemaId || !hasFormattedAnswerFilters) return;

      return FormSubmissionAdapter.answers.getListV2({
        page: pageParam ?? 1,
        pageSize: 500,
        sortBy,
        sortDirection,
        filters: answersQueryFilter as any,
        meta: {
          form_schema_id: formSchemaId,
        },
      });
    },
    getNextPageParam: (resp) => {
      return resp
        ? resp.page_number < resp.total_pages
          ? resp.page_number + 1
          : undefined
        : undefined;
    },
    enabled: Boolean(formSchemaId) && hasFormattedAnswerFilters,
    staleTime: 2 * 60 * 1000,
  });

  const filteredSubmissionIdsByAnswerSearch = useMemo(
    () => getFilteredSubmissionIdsByAnswerSearch(answersQuery),
    [answersQuery?.pages]
  );

  const queryPayloadWithoutAnswerFilter: Parameters<
    typeof FormSubmissionAdapter.getList
  >[0] = {
    page: pagination.page,
    pageSize: pagination.pageSize,
    sortBy,
    sortDirection,
    filters: {
      created_by,
      created_at,
      dataset_submission_id,
      ...toFlattenedObject({
        status: isDraft
          ? FormSubmissionStatus.DRAFT
          : status
            ? status
            : Object.values(FormSubmissionStatus).filter(
                (s) => s !== FormSubmissionStatus.DRAFT
              ),
        "form_schema.status": "active",
      }),
      ...(formSchemaId ? { "form_schema.id": formSchemaId } : {}),
    },
    meta: { answers: true },
  };

  const queryPayload = useMemo(
    () =>
      getFormSubmissionQueryPayloadWithAnswerFilter(
        queryPayloadWithoutAnswerFilter,
        hasFormattedAnswerFilters,
        filteredSubmissionIdsByAnswerSearch
      ),
    [
      queryPayloadWithoutAnswerFilter,
      hasFormattedAnswerFilters,
      filteredSubmissionIdsByAnswerSearch,
    ]
  );

  const formSubmissionsQuery = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, queryPayload],
    queryFn: () => {
      if (
        !formSchemaId ||
        (hasFormattedAnswerFilters &&
          (answersQueryIsLoading || answersQueryIsFetching))
      )
        return;

      return FormSubmissionAdapter.getList(queryPayload);
    },
    enabled:
      Boolean(formSchemaId) &&
      (!hasFormattedAnswerFilters ||
        (!answersQueryIsLoading && !answersQueryIsFetching)),
    staleTime: 2 * 60 * 1000,
  });

  const [bulkDeleteFormSubmissionsJobStorageKey] = useStorageKey(
    ["async-job"],
    "form-submissions-bulk-delete"
  );

  const { handleBulkDelete } = useBulkDeleteFormSubmissions({
    jobKey: bulkDeleteFormSubmissionsJobStorageKey,
    formSubmissionGetListParams: queryPayload,
    selectedFormSubmissions,
    isSelectionInverted,
    setIsDeleting,
  });

  const formSubmissionsCreatedByUsers = useQueries<
    Array<
      UseQueryOptions<
        Awaited<ReturnType<typeof UsersAdapter.getOne>> | undefined,
        unknown,
        UserReturnType | undefined
      >
    >
  >({
    queries:
      formSubmissionsQuery?.data?.data.map((submission) => ({
        queryKey: ["users", submission.created_by],
        queryFn: () =>
          UsersAdapter.getOne({
            id: submission.created_by ?? "",
          }),
        enabled: Boolean(submission.created_by),
        staleTime: 3 * 60 * 1000,
        select: (resp) => resp?.data,
      })) ?? [],
  });

  const formSubmissionsCreatedByUsersMap = useMemo(() => {
    return formSubmissionsCreatedByUsers.reduce<Record<string, UserReturnType>>(
      (map, q) => {
        if (!q.data?.id || map[q.data.id]) {
          return map;
        }

        map[q.data.id] = q.data;
        return map;
      },
      {}
    );
  }, [formSubmissionsCreatedByUsers]);

  const bulkExportXLSXMutation = useExportFormSubmissions(queryPayload);
  const exportPDFMutation = useExportFormSubmissionAsPDF({
    includeEmptyAnswers: isDraft,
    showUpdatedAt: isDraft,
    metaUserDataMap: formSubmissionsCreatedByUsersMap,
  });
  const bulkExportPDFMutation = useBulkExportFormSubmissionAsPDF();
  const {
    measurementUnits,
    getPrecisionByType,
    isLoading: isMeasurementTypesLoading,
  } = useMeasurementTypes();

  // remove Delete action when no rows are selected
  const actionRowWhenNoRowsSelected = useMemo(
    () => [
      <Button
        key="export-pdfs"
        variant="outline"
        onClick={() => {
          setDownloadSelection({
            form_submission_ids: [],
          });
          window.open(
            FormSubmissionsDownloadPDFsRoute.toLink({
              pathParams: {
                categoryId: formCategoryId,
                formTemplateId: formSchemaId,
              },
              queryParams: {
                submissionType: isDraft ? "draft" : "all",
              },
            }),
            "_blank"
          );
        }}
      >
        Export PDFs
      </Button>,
      <ButtonWithPopover
        key="export"
        label="Export"
        variant="outline"
        icon="arrow-square-out"
        isLoading={bulkExportXLSXMutation.isLoading}
        onClick={() => bulkExportXLSXMutation.mutate()}
      />,
      isDraft || !isDataIngestionEnabled ? null : (
        <ImportDataAction
          key="import-form-template-submission"
          resource={{
            ...ResourceDefinitions.form_schema,
            label: {
              singular: "Form Template Submission",
              plural: "Form Template Submissions",
            },
          }}
          resourceId={formSchemaIdFilter}
        />
      ),
    ],
    [
      formCategoryId,
      formSchemaIdFilter,
      formSchemaId,
      isDraft,
      isDataIngestionEnabled,
      bulkExportXLSXMutation,
    ]
  );

  const dynamicSubmissionsHeaders = useMemo(
    () =>
      schemaQuery.data
        ? getHeadersFromSchema(schemaQuery.data, {
            measurementUnits,
            getPrecisionByType,
          })
        : [],
    [schemaQuery.data, measurementUnits, getPrecisionByType]
  );

  const isLoading =
    isMeasurementTypesLoading ||
    schemaQuery.isLoading ||
    formSubmissionsQuery.isLoading ||
    (hasFormattedAnswerFilters && answersQueryIsLoading) ||
    isDeleting;

  const submissionsHeaders: Array<HeaderType<FormSubmissionType>> = [
    {
      label: "Name",
      key: "id",
      renderComponent: ({ item }) => (
        <RoutingLink
          to={
            isDraft
              ? linkToUpdateFormSubmission(
                  item?.form_schema?.form_category_id,
                  item?.form_schema?.id,
                  item?.id
                )
              : linkToFormSubmissionDetail(item.id)
          }
        >
          {`${item?.form_schema?.name} - ${item?.id?.slice(0, 7)}`}
        </RoutingLink>
      ),
    },
    ...(!isDraft
      ? [
          {
            label: "Status",
            key: "status",
            isSortable: true,
            renderComponent: ({ item }: { item: FormSubmissionType }) => (
              <Pill
                variant={getStatusType(item.status, "form").type}
                isLoading={isLoading}
              >
                {toTitleCaseString(item.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            ),
          },
        ]
      : []),
    {
      label: "Created At",
      key: "created_at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          withTime
        />
      ),
    },
    {
      label: "Last Saved At",
      key: "updated_at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.updated_at}
          withTime
        />
      ),
    },
    {
      label: isDraft ? "Saved By" : "Submitted By",
      key: "created_by",
      isSortable: true,
      renderComponent: ({ item }) =>
        item.created_by ? (
          <UserDataDisplay
            id={item.created_by}
            name={
              formSubmissionsCreatedByUsersMap[item.created_by]?.name ?? "n/a"
            }
            isLoading={isLoading}
          />
        ) : (
          "-"
        ),
    },
    ...dynamicSubmissionsHeaders,
  ];

  useEffect(() => {
    setTableState({ page: 1, pageSize: 25 });
  }, [isDraft]);

  useEffect(() => {
    if (answersQueryHasNextPage && !answersQueryIsFetching) {
      answersQueryFetchNextPage();
    }
  }, [answersQueryHasNextPage, answersQueryIsFetching]);

  return (
    <>
      <BulkUpdateProgressBanner
        jobStorageKey={bulkDeleteFormSubmissionsJobStorageKey}
        setSelectedItems={setSelectedFormSubmissions}
        setIsRunning={setIsDeleting}
        setIsSelectionInverted={setIsSelectionInverted}
        invalidate={() => {
          queryClient.invalidateQueries({
            queryKey: [Resources.FORM_SUBMISSION],
          });
        }}
        subject={{
          singular: ResourceDefinitions.form_submission.label.singular,
          plural: ResourceDefinitions.form_submission.label.plural,
        }}
        action={"delete"}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        filterComponent={
          <FormSubmissionsTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            filterDrawerContentSlot={
              <FormSubmissionsTableFilterAreaDrawerContent
                hasStatusFilter
                hasSubmittedByFilter
                formSchemaId={formSchemaId}
                hasDatasetSubmissionIdFilter
              />
            }
          />
        }
        panelProps={{
          title: isDraft ? "Draft Form Submissions" : "Form Submissions",
          loaded: !schemaQuery.isLoading && !isMeasurementTypesLoading,
          actionRow: actionRowWhenNoRowsSelected,
        }}
        dataTableProps={{
          headers: submissionsHeaders,
          isLoading: isLoading,
          isBusy:
            schemaQuery.isFetching ||
            formSubmissionsQuery.isFetching ||
            (hasFormattedAnswerFilters && answersQueryIsFetching),
          items: formSubmissionsQuery?.data?.data ?? [],
          pagination: {
            ...pagination,
            total: formSubmissionsQuery.data?.total_entries,
          },
          sorting: {
            sortBy,
            sortDirection,
          },
          selected: selectedFormSubmissions,
          isSelectionInverted: isSelectionInverted,
          onSelectionChange: (value, shouldInvertSelection) => {
            setSelectedFormSubmissions(value);
            setIsSelectionInverted(shouldInvertSelection ?? false);
          },
          onSortChange: setTableState,
          onPaginationChange: setTableState,
          getItemId: (item) => item.id,
          getItemBulkActions: ({ buttonProps }) => [
            canDeleteFormSubmissions ? (
              <Button
                {...buttonProps}
                key="delete"
                variant="error-outline"
                slotLeft={<Icon variant="trash" />}
                isLoading={isDeleting}
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete
              </Button>
            ) : null,
            <DropdownMenu
              key="export-action"
              options={[
                {
                  label: "Export as XLSX",
                  onClick: () => bulkExportXLSXMutation.mutate(),
                },
                {
                  label: `Export as PDF`,
                  //selectedFormSubmissions is empty when user selects all items in the table
                  isDisabled:
                    (isSelectionInverted &&
                    formSubmissionsQuery.data?.total_entries
                      ? formSubmissionsQuery.data?.total_entries
                      : Object.keys(selectedFormSubmissions).length) <= 0,
                  onClick: () => {
                    if (
                      !isSelectionInverted &&
                      Object.keys(selectedFormSubmissions).length > 0
                    ) {
                      setDownloadSelection({
                        form_submission_ids: Object.keys(
                          selectedFormSubmissions
                        ),
                      });
                    }
                    window.open(
                      FormSubmissionsDownloadPDFsRoute.toLink({
                        pathParams: {
                          categoryId: formCategoryId,
                          formTemplateId: formSchemaId,
                        },
                        queryParams: {
                          submissionType: isDraft ? "draft" : "all",
                        },
                      }),
                      "_blank"
                    );
                  },
                },
              ]}
            >
              <Button
                {...buttonProps}
                icon="caret-down"
                iconPosition="right"
                isLoading={
                  exportPDFMutation.isLoading ||
                  bulkExportXLSXMutation.isLoading ||
                  bulkExportPDFMutation.isLoading
                }
              >
                Export
              </Button>
            </DropdownMenu>,
          ],
          getItemActions: ({
            item,
          }: {
            item: FormSubmissionType;
          }): ItemAction[] => [
            ...(item?.status !== FormSubmissionStatus.DRAFT &&
            item?.form_schema?.form_submission_editable &&
            canEditFormSubmissions
              ? ([
                  {
                    label: "Edit",
                    buttonProps: {
                      icon: "pencil-simple",
                      onClick: () =>
                        navigate({
                          pathname: linkToUpdateFormSubmission(
                            item?.form_schema?.form_category_id,
                            item?.form_schema?.id,
                            item?.id
                          ),
                        }),
                    },
                  },
                ] satisfies ItemAction[])
              : []),
            {
              label: "Export as PDF",
              buttonProps: {
                icon: "arrow-square-out",
                isLoading: exportPDFMutation.isLoading,
                onClick: () => {
                  exportPDFMutation.mutate(item);
                },
              },
            },
          ],
        }}
        columnConfigurationProps={{
          onSaveColumnConfigurationAsDefault: (colsToSave) =>
            saveDefaultConfigurationMutation
              .mutateAsync({
                name: "Default for Form Submissions with answers list table",
                configuration: {
                  columns: colsToSave,
                },
              })
              .then(() => true)
              .catch(() => false),
          isLoadingSaveDefaultColumnConfiguration:
            saveDefaultConfigurationMutation.isLoading,
          isSaveDefaultColumnConfigurationAvailable:
            isSaveConfigurationAvailable,
          onResetColumnConfiguration: () =>
            resetConfigurationMutation.mutateAsync(),
          onDeleteDefaultColumnConfiguration: () =>
            deleteDefaultConfigurationMutation
              .mutateAsync()
              .then(() => true)
              .catch(() => false),
          isLoadingDeleteDefaultColumnConfiguration:
            deleteDefaultConfigurationMutation.isLoading,
          isDeleteDefaultColumnConfigurationAvailable:
            isDeleteConfigurationAvailable,
          isLoadingDefaultColumnConfiguration:
            isLoadingTableUserInterfaceConfiguration,
          isDefaultColumnConfigurationAvailable: !!matchingDefaultConfiguration,
          defaultColumnConfiguration:
            matchingDefaultConfiguration?.configuration?.columns,
        }}
      />
      <BulkDeleteFormSubmissionDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
        }}
        handleBulkDelete={handleBulkDelete}
        formSubmissionsCount={
          isSelectionInverted
            ? (formSubmissionsQuery.data?.total_entries ?? 0)
            : Object.keys(selectedFormSubmissions).length
        }
      />
    </>
  );
};
